import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Dialog, DialogContent, Icon, Input, Label } from "@sagot/ui";
import { useId, useState } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useZohoSyncMutations } from "../use-zoho-sync";
import {
  draftOf,
  fieldKey,
  fieldsOf,
  mappingOf,
  objectsWithFields,
  problemsOf,
  sameDraft,
  standardDraft,
  type MappingDraft,
  type SyncField,
  type ZohoSync,
} from "../zoho-sync-model";
import { ZOHO_I18N } from "./zoho-card-parts";

const K = `${ZOHO_I18N}.contactsSync`;

interface RowProps {
  field: SyncField;
  value: string;
  problem: string | undefined;
  disabled: boolean;
  onChange: (value: string) => void;
}

/** One Sagot field and the Zoho API name it is kept in step with; suggestions come from a list, a custom name may be typed. */
function MappingRow({ field, value, problem, disabled, onChange }: RowProps) {
  const { t } = useTranslation();
  const id = useId();
  const listId = `${id}-list`;
  const label = t(`${K}.fields.${fieldKey(field.local)}`);
  return (
    <li className="grid gap-2 py-3 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] md:items-start" data-testid={`zoho-map-row-${field.local}`}>
      <div className="min-w-0">
        <Label htmlFor={id}>{label}</Label>
        {field.protected ? (
          <Badge tone="brand" className="mt-1" data-testid={`zoho-map-protected-${field.local}`}>
            <Icon name="lock" className="size-3" />
            {t(`${K}.mappingDialog.sendOnly`)}
          </Badge>
        ) : null}
      </div>
      <div className="min-w-0">
        <Input
          id={id}
          list={listId}
          autoComplete="off"
          spellCheck={false}
          className="font-mono"
          value={value}
          disabled={disabled}
          placeholder={t(`${K}.mappingDialog.notMapped`)}
          aria-invalid={problem ? true : undefined}
          aria-describedby={problem ? `${id}-problem` : undefined}
          onChange={(event) => { onChange(event.target.value); }}
          data-testid={`zoho-map-${field.local}`}
        />
        <datalist id={listId}>
          {field.suggestions.map((name) => <option key={name} value={name} />)}
        </datalist>
        {problem ? (
          <p id={`${id}-problem`} role="alert" className="mt-1.5 text-xs font-semibold text-danger">
            {t(`${K}.mappingDialog.${problem === "name" ? "problemName" : "problemDuplicate"}`)}
          </p>
        ) : null}
      </div>
    </li>
  );
}

interface DialogProps {
  sync: ZohoSync;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Field mapping (CRM-006): which Zoho field each Sagot field of a contact and an account is kept in step with. Customer reference, tier and account status can only be sent. */
export function ZohoFieldMappingDialog({ sync, open, onOpenChange }: DialogProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const { save } = useZohoSyncMutations();
  const saved = draftOf(sync.mapping);
  const [draft, setDraft] = useState<MappingDraft>(saved);
  const [failure, setFailure] = useState<string | null>(null);
  const problems = problemsOf(draft, sync.fields);
  const dirty = !sameDraft(draft, saved, sync.fields);
  const blocked = Object.keys(problems).length > 0 || !sync.can_manage;
  const close = (next: boolean) => {
    if (!next) {
      setDraft(saved);
      setFailure(null);
    }
    onOpenChange(next);
  };
  const submit = () => {
    setFailure(null);
    save.mutate(
      { enabled: sync.enabled, direction: sync.direction, mapping: mappingOf(draft, sync.fields) },
      { onSuccess: () => { onOpenChange(false); }, onError: (error) => { setFailure(message(error)); } },
    );
  };
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        title={t(`${K}.mappingDialog.title`)}
        description={t(`${K}.mappingDialog.description`)}
        closeLabel={t("common.close")}
        className="max-w-2xl"
        footer={
          <>
            <Button variant="ghost" disabled={!sync.can_manage} onClick={() => { setDraft(standardDraft(sync.fields)); }} data-testid="zoho-map-reset">
              <Icon name="rotate-ccw" />
              {t(`${K}.mappingDialog.reset`)}
            </Button>
            <Button variant="secondary" onClick={() => { close(false); }}>{t("common.cancel")}</Button>
            <Button loading={save.isPending} disabled={blocked || !dirty} onClick={submit} data-testid="zoho-map-save">
              {save.isPending ? t(`${K}.mappingDialog.saving`) : t(`${K}.mappingDialog.save`)}
            </Button>
          </>
        }
      >
        <div className="space-y-5" data-testid="zoho-map-dialog">
          {objectsWithFields(sync.fields).map((object) => (
            <fieldset key={object}>
              <legend className="text-sm font-semibold">{t(`${K}.mappingDialog.${object}Group`)}</legend>
              <ul className="divide-y divide-line">
                {fieldsOf(sync.fields, object).map((field) => (
                  <MappingRow
                    key={field.local}
                    field={field}
                    value={draft[field.local] ?? ""}
                    problem={problems[field.local]}
                    disabled={!sync.can_manage}
                    onChange={(value) => { setDraft({ ...draft, [field.local]: value }); setFailure(null); }}
                  />
                ))}
              </ul>
            </fieldset>
          ))}
          <p className="text-xs text-fg-muted">{t(`${K}.mappingDialog.referenceHint`)}</p>
          {failure ? <p role="alert" className="text-xs font-semibold text-danger" data-testid="zoho-map-error">{failure}</p> : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
